import { useState } from "react";
import { bookingServices } from "../../../mocks/bookingData";
import type { BookingItemData } from "../../../mocks/bookingData";
import type { BookingState } from "../page";

interface Step2BinCollectionProps {
  booking: BookingState;
  onUpdate: (updates: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

function isLondonPostcode(pc: string) {
  const upper = pc.trim().toUpperCase();
  const londonPrefixes = ["EC","WC","E","N","NW","W","SW","SE","HA","UB","BR","CR","EN","IG","KT","RM","SM","TW","WD"];
  return londonPrefixes.some((p) => upper.startsWith(p));
}

function getPostcodeSurcharge(pc: string): number {
  if (!pc) return 0;
  if (isLondonPostcode(pc)) return 10;
  return 0;
}

function formatPrice(p: number) {
  return Number.isInteger(p) ? `£${p}` : `£${p.toFixed(2)}`;
}

export default function Step2BinCollection({ booking, onUpdate, onNext, onBack }: Step2BinCollectionProps) {
  const [postcodeInput, setPostcodeInput] = useState(booking.postcode || "");
  const [postcodeApplied, setPostcodeApplied] = useState(!!booking.postcode);
  const [postcodeError, setPostcodeError] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const serviceConfig = bookingServices.find((s) => s.slug === booking.serviceSlug);
  const itemsConfig: BookingItemData[] = serviceConfig?.itemsConfig ?? [];
  const calloutCharge = serviceConfig?.calloutCharge ?? 0;

  const surcharge = postcodeApplied ? getPostcodeSurcharge(postcodeInput) : 0;

  const getPrice = (item: BookingItemData) => item.unitPrice + surcharge;
  const getQty = (id: string) => booking.cartItems.find((c) => c.id === id)?.quantity ?? 0;

  const updateQty = (item: BookingItemData, delta: number) => {
    const price = getPrice(item);
    const existing = booking.cartItems.find((c) => c.id === item.id);
    if (!existing && delta > 0) {
      onUpdate({ cartItems: [...booking.cartItems, { id: item.id, label: item.label, quantity: 1, unitPrice: price }] });
    } else if (existing) {
      const newQty = existing.quantity + delta;
      if (newQty <= 0) {
        onUpdate({ cartItems: booking.cartItems.filter((c) => c.id !== item.id) });
      } else {
        onUpdate({ cartItems: booking.cartItems.map((c) => c.id === item.id ? { ...c, quantity: newQty, unitPrice: price } : c) });
      }
    }
    setError("");
  };

  const handleGetPrices = () => {
    if (!postcodeInput.trim()) {
      setPostcodeError("Please enter your collection postcode");
      return;
    }
    setChecking(true);
    setTimeout(() => {
      setChecking(false);
      setPostcodeApplied(true);
      setPostcodeError("");
      const pc = postcodeInput.trim().toUpperCase();
      onUpdate({
        postcode: pc,
        cartItems: booking.cartItems.map((c) => {
          const item = itemsConfig.find((i) => i.id === c.id);
          if (!item) return c;
          return { ...c, unitPrice: item.unitPrice + getPostcodeSurcharge(pc) };
        }),
      });
    }, 800);
  };

  const cartTotal = booking.cartItems.reduce((s, c) => s + c.quantity * c.unitPrice, 0);
  const cartCount = booking.cartItems.reduce((s, c) => s + c.quantity, 0);
  const grandTotal = cartTotal + (cartCount > 0 ? calloutCharge : 0);

  const handleContinue = () => {
    if (booking.cartItems.length === 0) {
      setError("Please select at least one bin to continue");
      return;
    }
    onNext();
  };

  return (
    <div className="w-full">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-bold text-cl-dark/50 hover:text-cl-dark transition-colors cursor-pointer mb-6">
        <i className="ri-arrow-left-line"></i> Back
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-black text-cl-dark">Select Bin Size</h1>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4].map((s) => (
                <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>
              ))}
              <i className="ri-star-half-fill text-amber-400 text-sm"></i>
            </div>
            <span className="text-xs font-bold text-cl-dark">4.8 out of 5</span>
            <span className="text-xs text-gray-400 font-semibold">based on 36,722 reviews</span>
          </div>
          <span className="text-xs text-gray-400 font-semibold hidden sm:inline">Waste carrier&apos;s licence: CBDU 634440</span>
        </div>
      </div>

      <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-6 thick-border cartoon-shadow-sm">
        <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Step 2 of 5</span>
      </div>

      {/* Callout charge banner */}
      {calloutCharge > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border-2 border-amber-300 rounded-2xl px-5 py-4 mb-6">
          <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-400 text-white flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-sm"></i>
          </span>
          <div>
            <p className="font-black text-cl-dark text-sm">£{calloutCharge} single callout charge applies</p>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              A one-time £{calloutCharge} callout fee is added to your order, regardless of how many bins are collected.
            </p>
          </div>
        </div>
      )}

      <div className={`rounded-2xl border-2 p-5 mb-7 transition-all ${postcodeApplied ? "bg-[#f0fce4] border-cl-green" : "bg-white border-transparent"}`}>
        {postcodeApplied ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-cl-green border-2 border-[#4a8c0a] text-white flex-shrink-0">
                <i className="ri-map-pin-2-fill text-sm"></i>
              </span>
              <div>
                <p className="text-sm font-black text-cl-dark">
                  Prices for: <span className="text-cl-green">{postcodeInput.toUpperCase()}</span>
                  {surcharge > 0 && <span className="ml-2 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">+£{surcharge} area surcharge applied</span>}
                </p>
                <p className="text-xs text-cl-dark/60 font-semibold">Prices updated for your location</p>
              </div>
            </div>
            <button
              onClick={() => { setPostcodeApplied(false); setPostcodeInput(""); onUpdate({ postcode: "", cartItems: [] }); }}
              className="text-xs font-black text-cl-dark/50 hover:text-cl-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Change postcode
            </button>
          </div>
        ) : (
          <>
            <p className="font-black text-cl-dark text-sm mb-1">Postcode<span className="text-red-500">*</span></p>
            <p className="text-xs text-gray-400 font-semibold mb-3">Enter collection postcode to get accurate prices for your area</p>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <i className="ri-map-pin-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={postcodeInput}
                  onChange={(e) => { setPostcodeInput(e.target.value); setPostcodeError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleGetPrices()}
                  placeholder="Enter collection postcode"
                  className={`w-full border-2 rounded-xl pl-9 pr-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${postcodeError ? "border-red-400" : "border-transparent focus:border-cl-dark"}`}
                />
              </div>
              <button
                onClick={handleGetPrices}
                disabled={checking}
                className="bg-cl-green text-white font-black px-6 py-3 rounded-xl text-sm cartoon-btn-green whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                {checking ? <><i className="ri-loader-4-line animate-spin"></i> Checking…</> : "Get Prices"}
              </button>
            </div>
            {postcodeError && <p className="text-xs text-red-500 mt-2 font-bold flex items-center gap-1"><i className="ri-error-warning-line"></i>{postcodeError}</p>}
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl border-2 border-transparent overflow-hidden mb-6">
        {itemsConfig.map((item, idx) => {
          const qty = getQty(item.id);
          const price = getPrice(item);
          return (
            <div
              key={item.id}
              className={`flex items-center gap-4 px-5 py-4 transition-colors ${idx < itemsConfig.length - 1 ? "border-b-2 border-gray-100" : ""} ${qty > 0 ? "bg-[#f6fff0]" : "hover:bg-gray-50/60"}`}
            >
              <span className={`w-10 h-10 flex items-center justify-center rounded-xl border-2 text-lg flex-shrink-0 ${qty > 0 ? "bg-cl-green border-[#4a8c0a] text-white" : "bg-cl-lime border-cl-green text-cl-green"}`}>
                <i className={item.icon}></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-black text-cl-dark text-sm leading-snug">{item.label}</p>
                <p className={`text-base font-black leading-tight ${qty > 0 ? "text-cl-green" : "text-cl-dark"}`}>
                  {formatPrice(price)} <span className="text-xs text-gray-400 font-semibold">inc. VAT</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onPointerUp={(e) => { e.preventDefault(); updateQty(item, -1); }} disabled={qty === 0}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl border-2 font-black text-sm transition-all cursor-pointer ${qty === 0 ? "border-transparent text-gray-300 cursor-not-allowed" : "border-cl-dark text-cl-dark hover:bg-cl-dark hover:text-white"}`}>
                  <i className="ri-subtract-line"></i>
                </button>
                <span className={`w-7 text-center font-black text-sm ${qty > 0 ? "text-cl-green" : "text-gray-400"}`}>{qty}</span>
                <button onPointerUp={(e) => { e.preventDefault(); updateQty(item, 1); }}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                  className="w-9 h-9 flex items-center justify-center rounded-xl border-2 border-cl-dark text-cl-dark hover:bg-cl-dark hover:text-white font-black text-sm transition-all cursor-pointer">
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {cartCount > 0 && (
        <div className="mb-4 bg-cl-dark rounded-2xl px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-white/70 text-xs font-semibold">{cartCount} bin(s) selected</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {booking.cartItems.slice(0,3).map((c) => (
                <span key={c.id} className="text-xs text-cl-lime font-bold">{c.label}{c.quantity > 1 ? ` x${c.quantity}` : ""}</span>
              ))}
              {booking.cartItems.length > 3 && <span className="text-xs text-white/50 font-semibold">+{booking.cartItems.length - 3} more</span>}
            </div>
            {calloutCharge > 0 && (
              <p className="text-xs text-amber-300 font-semibold mt-1">+ £{calloutCharge} callout charge</p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-cl-lime font-black text-xl">{formatPrice(grandTotal)}</p>
            {calloutCharge > 0 && <p className="text-white/50 text-xs font-semibold">inc. callout</p>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-4 border-2 border-transparent flex items-center gap-4 mb-6">
        <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0">
          <i className="ri-question-line text-sm"></i>
        </span>
        <p className="text-sm text-gray-500 font-semibold flex-1">
          Not sure which size? Call us on <a href="tel:03335775445" className="text-cl-green font-black">03335 775445</a> — we&apos;ll help you pick the right bin.
        </p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm font-bold text-red-500 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="flex items-center justify-end">
        <button onClick={handleContinue}
          className="bg-cl-green text-white font-black px-10 py-4 rounded-xl text-base cartoon-btn-green whitespace-nowrap cursor-pointer">
          Continue <i className="ri-arrow-right-line ml-1"></i>
        </button>
      </div>
    </div>
  );
}
